import type { MessageKey } from "@/i18n/catalog";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, type Page } from "./client";

/**
 * The derived balance (`model.md` §4.1). Three terms, kept apart.
 *
 * There is no stored balance and no single number: "your balance is €412,30"
 * and "that is €500,00 you started with, less €87,70 since" are different
 * answers, and the second is the one that settles an argument with a bank
 * statement.
 */
export interface Balance {
  currency_code: string;
  opening_minor: number;
  movement_minor: number;
  total_minor: number;
  as_of: string;
}

export interface Account {
  id: string;
  name: string;
  kind: AccountKind;
  currency_code: string;
  opening_balance_minor: number;
  opening_date: string;
  institution_name?: string;
  iban_last4?: string;
  is_archived: boolean;
  sort_order: number;
  balance: Balance;
  created_at: string;
  updated_at: string;
}

export type AccountKind =
  | "checking"
  | "savings"
  | "cash"
  | "credit_card"
  | "asset"
  | "liability";

/** The kinds, in the order a picker should show them, with German labels. */
/**
 * The kinds of account, in the order a picker should offer them.
 *
 * They carry a message key rather than a label: what an account *is* belongs to
 * the API's vocabulary, and what it is *called* belongs to whoever is reading
 * (K-36). `accountKindLabel` turns one into the other.
 */
export const ACCOUNT_KINDS: Array<{ value: AccountKind; key: MessageKey }> = [
  { value: "checking", key: "accountKind.checking" },
  { value: "savings", key: "accountKind.savings" },
  { value: "cash", key: "accountKind.cash" },
  { value: "credit_card", key: "accountKind.credit_card" },
  { value: "asset", key: "accountKind.asset" },
  { value: "liability", key: "accountKind.liability" },
];

export const accountsQueryKey = (householdId: string) =>
  ["households", householdId, "accounts"] as const;

/** Lists the household's accounts with their balances. */
export function useAccounts(householdId: string | undefined) {
  return useQuery({
    queryKey: accountsQueryKey(householdId ?? ""),
    queryFn: ({ signal }) =>
      apiFetch<Page<Account>>(`/api/v1/households/${householdId}/accounts`, { signal }),
    enabled: Boolean(householdId),
  });
}

export interface CreateAccountInput {
  name: string;
  kind: AccountKind;
  currency_code: string;
  opening_balance_minor: number;
  opening_date: string;
  institution_name?: string;
}

export function useCreateAccount(householdId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAccountInput) =>
      apiFetch<Account>(`/api/v1/households/${householdId}/accounts`, {
        method: "POST",
        body: input,
      }),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: accountsQueryKey(householdId) }),
  });
}
